import React, { useState } from 'react';

const DEMO_BASE = 'https://apn-demos.vercel.app';

// ---------- SEED DATA ----------
const PROSPECTS = [
  {
    id: 'prs1',
    name: "Sergio's Pizza",
    phone: '(203) 967-4200',
    status: 'PHONE_VERIFIED',
    demoSlug: '/sergio-s-pizza-restaurant',
    outreach: 'Not contacted',
    dossier: 'Family-owned pizzeria. Decades in Stamford. No website currently. Good Google reviews.',
    timeline: [
      { date: '2026-03-28', event: 'Demo site generated' },
      { date: '2026-03-29', event: 'Phone verified via lookup' },
    ],
  },
  {
    id: 'prs2',
    name: 'Magic Scissors',
    phone: '(203) 487-0118',
    status: 'PHONE_VERIFIED',
    demoSlug: '/magic-scissors-beauty-salon',
    outreach: 'Not contacted',
    dossier: 'Beauty salon with solid local following. Booking handled by phone only. Prime for booking widget.',
    timeline: [
      { date: '2026-03-28', event: 'Demo site generated' },
      { date: '2026-03-29', event: 'Phone verified via lookup' },
    ],
  },
  {
    id: 'prs3',
    name: 'Ginter Heating',
    phone: '(203) 329-0211',
    status: 'PHONE_VERIFIED',
    demoSlug: '/ginter-heating-a-c',
    outreach: 'Not contacted',
    dossier: 'HVAC company. Seasonal demand spikes. High emergency call volume - phone agent would pay for itself fast.',
    timeline: [
      { date: '2026-03-28', event: 'Demo site generated' },
      { date: '2026-03-29', event: 'Phone verified via lookup' },
    ],
  },
];

const LEADS = [
  {
    id: 'ld1',
    name: 'A to Z Property Maintenance',
    owner: 'Daimeon Baity',
    tierInterest: 'Starter',
    status: 'warm',
    demoSlug: '/a-to-z-property-maintenance-llc',
    contractStatus: 'Draft',
    paymentStatus: 'Unpaid',
    paymentLink: 'https://buy.stripe.com/placeholder_ato_z',
    notes: 'Warm lead. Responded positive on text, waiting on confirmation to send contract.',
  },
  {
    id: 'ld2',
    name: 'Amore Cucina & Bar',
    owner: 'Amore Cucina & Bar',
    tierInterest: 'Professional',
    status: 'live_pitch',
    demoSlug: '/amore-cucina-bar',
    contractStatus: 'Not sent',
    paymentStatus: 'Unpaid',
    paymentLink: 'https://buy.stripe.com/placeholder_amore',
    notes: 'Live pitch scheduled 4/1. Awaiting callback to finalize time.',
  },
];

const CLIENTS = [
  {
    id: 'cl1',
    name: 'Minute Man Land Services',
    owner: 'Jordan Grant',
    status: 'contract_sent',
    contractStatus: 'Sent - awaiting signature',
    paid: false,
    stripeCustomerId: 'cus_UFHQSJFD2NSLhY',
    retainerLink: 'https://buy.stripe.com/00w6oHbG8feN3lo4n68so01',
    liveSite: '',
    domain: 'minutemenlandscaping.com',
    emailAccounts: [],
    monthlyCost: 18,
    monthlyRevenue: 79,
    changeRequests: [],
    comments: [
      { author: 'Nicholas', date: '2026-03-30', text: 'Retainer link sent via text. Follow up tomorrow.' },
    ],
    payments: [],
  },
  {
    id: 'cl2',
    name: 'ROOTZ Salon and Spa',
    owner: 'Margaret & Latisha',
    status: 'contract_sent',
    contractStatus: 'Sent - awaiting signature',
    paid: false,
    stripeCustomerId: 'cus_UH9DNtjGSQ6mhS',
    retainerLink: 'https://buy.stripe.com/6oU5kDaC4c2B4psg5O8so00',
    liveSite: '',
    domain: '',
    emailAccounts: [],
    monthlyCost: 18,
    monthlyRevenue: 199,
    changeRequests: [],
    comments: [
      { author: 'Caroline', date: '2026-03-29', text: 'Both owners on board. Waiting on retainer payment.' },
    ],
    payments: [],
  },
];

const PIPELINE_STAGES = [
  { key: 'contract_sent', label: 'Contract Sent' },
  { key: 'retainer_paid', label: 'Retainer Paid' },
  { key: 'domain_setup', label: 'Domain Setup' },
  { key: 'photos_gathered', label: 'Photos Gathered' },
  { key: 'site_polished', label: 'Site Polished' },
  { key: 'client_review', label: 'Client Review' },
  { key: 'live', label: 'Live' },
  { key: 'completion_paid', label: 'Completion Paid' },
  { key: 'monthly_active', label: 'Monthly Active' },
];

// ---------- HELPERS ----------
const BADGE_CLASSES = {
  red: 'bg-red-100 text-red-700 border-red-200',
  yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  blue: 'bg-blue-100 text-apn-blue border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  gray: 'bg-gray-100 text-gray-600 border-gray-200',
};

const Badge = ({ kind = 'gray', children }) => (
  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${BADGE_CLASSES[kind] || BADGE_CLASSES.gray}`}>
    {children}
  </span>
);

const CopyButton = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };
  return (
    <button
      onClick={handleCopy}
      className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors whitespace-nowrap ${
        copied
          ? 'bg-green-50 text-green-700 border-green-200'
          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
      }`}
    >
      {copied ? 'Copied!' : label}
    </button>
  );
};

const ActionButton = ({ children, onClick, variant = 'primary' }) => {
  const variants = {
    primary: 'bg-apn-blue text-white border-apn-blue hover:bg-apn-dark-blue',
    secondary: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
    success: 'bg-green-600 text-white border-green-600 hover:bg-green-700',
    warning: 'bg-yellow-500 text-white border-yellow-500 hover:bg-yellow-600',
  };
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
      className={`px-3.5 py-2 rounded-md text-xs font-semibold border transition-colors whitespace-nowrap ${variants[variant] || variants.primary}`}
    >
      {children}
    </button>
  );
};

const SectionLabel = ({ children }) => (
  <div className="text-xs font-bold text-apn-gray uppercase tracking-wider mb-2">{children}</div>
);

// ---------- PROSPECT VIEW ----------
function ProspectView() {
  const [expandedId, setExpandedId] = useState(null);

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-gray-900 m-0">Prospects</h2>
        <p className="text-sm text-gray-500 mt-1">
          Pre-contact targets. Phone verified and demo site ready.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {PROSPECTS.map(p => {
          const expanded = expandedId === p.id;
          const demoUrl = `${DEMO_BASE}${p.demoSlug}`;
          return (
            <div
              key={p.id}
              onClick={() => setExpandedId(expanded ? null : p.id)}
              className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 cursor-pointer hover:border-apn-light-blue transition-colors"
            >
              <div className="flex flex-wrap gap-3 justify-between items-start">
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold text-gray-900">{p.name}</div>
                  <div className="text-sm text-gray-500 mt-0.5">{p.phone}</div>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  <Badge kind="green">{p.status}</Badge>
                  <Badge kind="gray">{p.outreach}</Badge>
                </div>
              </div>

              <div className="mt-3">
                <a
                  href={demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs text-apn-blue hover:text-apn-dark-blue hover:underline"
                >
                  {p.demoSlug}
                </a>
              </div>

              <div className="mt-3 flex gap-2 flex-wrap">
                <ActionButton variant="primary" onClick={() => alert('Demo link sent: ' + demoUrl)}>
                  Send Demo Link
                </ActionButton>
                <ActionButton variant="secondary" onClick={() => alert('Generating contract for ' + p.name)}>
                  Generate Contract
                </ActionButton>
                <ActionButton variant="warning" onClick={() => alert('Creating payment link for ' + p.name)}>
                  Create Payment Link
                </ActionButton>
              </div>

              {expanded && (
                <div className="mt-4 pt-4 border-t border-gray-200 grid gap-4 md:grid-cols-2">
                  <div className="bg-gray-50 rounded-lg p-3.5 border border-gray-200">
                    <SectionLabel>Dossier</SectionLabel>
                    <p className="text-sm text-gray-700 leading-relaxed m-0">{p.dossier}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3.5 border border-gray-200">
                    <SectionLabel>Timeline</SectionLabel>
                    {p.timeline.map((t, i) => (
                      <div key={i} className="text-xs text-gray-700 mb-1.5">
                        <span className="text-gray-500">{t.date}</span> — {t.event}
                      </div>
                    ))}
                  </div>

                  <div className="md:col-span-2 bg-gray-50 rounded-lg p-3.5 border border-gray-200">
                    <SectionLabel>Demo Site Preview</SectionLabel>
                    <div className="border border-gray-300 rounded-md overflow-hidden h-96 bg-white">
                      <iframe src={demoUrl} title={`${p.name} demo`} className="w-full h-full border-0" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- LEAD VIEW ----------
function LeadView() {
  const [expandedId, setExpandedId] = useState(null);

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-gray-900 m-0">Leads</h2>
        <p className="text-sm text-gray-500 mt-1">Interested prospects — pre-payment.</p>
      </div>

      <div className="flex flex-col gap-3">
        {LEADS.map(l => {
          const expanded = expandedId === l.id;
          const demoUrl = `${DEMO_BASE}${l.demoSlug}`;
          const statusKind = l.status === 'warm' ? 'yellow' : 'purple';
          const statusLabel = l.status === 'warm' ? 'Warm' : 'Live Pitch';
          return (
            <div
              key={l.id}
              onClick={() => setExpandedId(expanded ? null : l.id)}
              className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 cursor-pointer hover:border-apn-light-blue transition-colors"
            >
              <div className="flex flex-wrap gap-3 justify-between items-start">
                <div className="flex-1 min-w-0">
                  <div className="text-base font-semibold text-gray-900">{l.name}</div>
                  <div className="text-sm text-gray-500 mt-0.5">{l.owner} · {l.tierInterest}</div>
                </div>
                <div className="flex gap-2 flex-wrap items-center">
                  <Badge kind={statusKind}>{statusLabel}</Badge>
                  <Badge kind={l.contractStatus === 'Not sent' ? 'red' : 'yellow'}>
                    Contract: {l.contractStatus}
                  </Badge>
                  <Badge kind="red">{l.paymentStatus}</Badge>
                </div>
              </div>

              <div className="mt-3">
                <a
                  href={demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs text-apn-blue hover:text-apn-dark-blue hover:underline"
                >
                  {l.demoSlug}
                </a>
              </div>

              <div className="mt-3 px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-200 flex gap-2.5 items-center flex-wrap">
                <span className="text-xs text-apn-gray font-semibold uppercase tracking-wide">Retainer Link</span>
                <code className="text-xs text-apn-blue flex-1 min-w-0 truncate">{l.paymentLink}</code>
                <CopyButton text={l.paymentLink} />
              </div>

              <div className="mt-3 flex gap-2 flex-wrap">
                <ActionButton variant="primary" onClick={() => alert('Sending contract to ' + l.name)}>
                  Send Contract
                </ActionButton>
                <ActionButton variant="warning" onClick={() => alert('Sending retainer link to ' + l.name)}>
                  Send Retainer Link
                </ActionButton>
                <ActionButton variant="secondary" onClick={() => alert('Scheduling Zoom with ' + l.name)}>
                  Schedule Zoom
                </ActionButton>
              </div>

              {expanded && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <SectionLabel>Notes</SectionLabel>
                  <p className="text-sm text-gray-700 leading-relaxed mt-0 mb-4">{l.notes}</p>

                  <SectionLabel>Site Preview</SectionLabel>
                  <div className="border border-gray-300 rounded-md overflow-hidden h-96 bg-white">
                    <iframe src={demoUrl} title={`${l.name} preview`} className="w-full h-full border-0" />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- CLIENT VIEW ----------
function ClientView() {
  const [selectedId, setSelectedId] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [commentsState, setCommentsState] = useState(
    CLIENTS.reduce((acc, c) => ({ ...acc, [c.id]: c.comments }), {})
  );

  if (selectedId) {
    const client = CLIENTS.find(c => c.id === selectedId);
    if (!client) { setSelectedId(null); return null; }
    const currentStageIdx = PIPELINE_STAGES.findIndex(s => s.key === client.status);
    const profit = client.monthlyRevenue - client.monthlyCost;
    const margin = client.monthlyRevenue > 0 ? Math.round((profit / client.monthlyRevenue) * 100) : 0;

    const addComment = () => {
      if (!newComment.trim()) return;
      const entry = {
        author: 'You',
        date: new Date().toISOString().slice(0, 10),
        text: newComment.trim(),
      };
      setCommentsState(prev => ({ ...prev, [client.id]: [...(prev[client.id] || []), entry] }));
      setNewComment('');
    };

    return (
      <div>
        <button
          onClick={() => setSelectedId(null)}
          className="mb-5 px-3.5 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
        >
          ← Back to Clients
        </button>

        <div className="mb-5">
          <h2 className="text-2xl font-bold text-gray-900 m-0">{client.name}</h2>
          <p className="text-sm text-gray-500 mt-1">{client.owner}</p>
        </div>

        <div className="flex gap-2 flex-wrap mb-6">
          <Badge kind={client.paid ? 'green' : 'red'}>{client.paid ? 'PAID' : 'NOT PAID'}</Badge>
          <Badge kind="yellow">Contract: {client.contractStatus}</Badge>
          <Badge kind="blue">Stripe: {client.stripeCustomerId}</Badge>
        </div>

        {/* Pipeline Visual */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 mb-4 overflow-x-auto">
          <SectionLabel>Pipeline</SectionLabel>
          <div className="flex gap-2 min-w-max">
            {PIPELINE_STAGES.map((stage, idx) => {
              const completed = idx < currentStageIdx;
              const current = idx === currentStageIdx;
              const cls = current
                ? 'bg-apn-blue text-white border-apn-blue'
                : completed
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-gray-50 text-gray-500 border-gray-200';
              return (
                <div
                  key={stage.key}
                  className={`flex-none px-3 py-2 rounded-md text-xs font-semibold border whitespace-nowrap ${cls}`}
                >
                  {idx + 1}. {stage.label}
                </div>
              );
            })}
          </div>
        </div>

        {/* Cost tracking */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white border border-gray-200 border-t-4 border-t-red-400 rounded-lg shadow-sm p-4">
            <div className="text-xs text-apn-gray font-bold uppercase tracking-wider">Monthly Cost</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">${client.monthlyCost}</div>
          </div>
          <div className="bg-white border border-gray-200 border-t-4 border-t-apn-blue rounded-lg shadow-sm p-4">
            <div className="text-xs text-apn-gray font-bold uppercase tracking-wider">Revenue</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">${client.monthlyRevenue}</div>
          </div>
          <div className="bg-white border border-gray-200 border-t-4 border-t-green-500 rounded-lg shadow-sm p-4">
            <div className="text-xs text-apn-gray font-bold uppercase tracking-wider">Profit</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">${profit}</div>
          </div>
          <div className="bg-white border border-gray-200 border-t-4 border-t-purple-500 rounded-lg shadow-sm p-4">
            <div className="text-xs text-apn-gray font-bold uppercase tracking-wider">Margin</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{margin}%</div>
          </div>
        </div>

        {/* Retainer link */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4">
          <SectionLabel>Retainer Payment Link</SectionLabel>
          <div className="flex gap-2.5 items-center flex-wrap">
            <code className="text-xs text-apn-blue flex-1 min-w-0 truncate">{client.retainerLink}</code>
            <CopyButton text={client.retainerLink} />
            <a
              href={client.retainerLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-apn-blue hover:text-apn-dark-blue font-semibold"
            >
              Open →
            </a>
          </div>
        </div>

        {/* Site & Domain info */}
        <div className="grid gap-3 md:grid-cols-3 mb-4">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <SectionLabel>Live Site</SectionLabel>
            <div className="text-sm text-gray-700">{client.liveSite || 'Not deployed yet'}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <SectionLabel>Domain</SectionLabel>
            <div className="text-sm text-gray-700">{client.domain || 'Not purchased'}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <SectionLabel>Email Accounts</SectionLabel>
            <div className="text-sm text-gray-700">
              {client.emailAccounts.length ? client.emailAccounts.join(', ') : 'None provisioned'}
            </div>
          </div>
        </div>

        {/* Payments */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4">
          <SectionLabel>Payments</SectionLabel>
          {client.payments.length === 0 ? (
            <div className="text-sm text-gray-400">No payments recorded yet.</div>
          ) : (
            client.payments.map((pay, i) => (
              <div key={i} className="text-sm text-gray-700 py-1.5 border-b border-gray-100 last:border-0">
                {pay.date} — ${pay.amount} — {pay.note}
              </div>
            ))
          )}
        </div>

        {/* Change requests */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4">
          <SectionLabel>Change Requests</SectionLabel>
          {client.changeRequests.length === 0 ? (
            <div className="text-sm text-gray-400">No change requests.</div>
          ) : (
            client.changeRequests.map((cr, i) => (
              <div key={i} className="text-sm text-gray-700 py-1.5">• {cr}</div>
            ))
          )}
        </div>

        {/* Comments */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
          <SectionLabel>Comments</SectionLabel>
          <div className="mb-3">
            {(commentsState[client.id] || []).map((c, i) => (
              <div key={i} className="py-2.5 border-b border-gray-100 last:border-0">
                <div className="text-xs text-gray-500 mb-1">
                  <strong className="text-gray-900">{c.author}</strong> · {c.date}
                </div>
                <div className="text-sm text-gray-700">{c.text}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <input
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 min-w-[220px] px-3 py-2.5 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-apn-light-blue focus:border-apn-blue"
            />
            <ActionButton variant="primary" onClick={addComment}>Post</ActionButton>
          </div>
        </div>
      </div>
    );
  }

  // Client list
  return (
    <div>
      <div className="mb-5">
        <h2 className="text-xl font-bold text-gray-900 m-0">Clients</h2>
        <p className="text-sm text-gray-500 mt-1">
          Full lifecycle dashboard for signed / active clients.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {CLIENTS.map(c => {
          const profit = c.monthlyRevenue - c.monthlyCost;
          const stage = PIPELINE_STAGES.find(s => s.key === c.status);
          return (
            <div
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              className={`bg-white border border-gray-200 rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md hover:border-apn-light-blue transition-all border-l-4 ${
                c.paid ? 'border-l-green-500' : 'border-l-red-400'
              }`}
            >
              <div className="flex justify-between items-start gap-2.5 mb-2.5">
                <div className="min-w-0">
                  <div className="text-base font-semibold text-gray-900">{c.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{c.owner}</div>
                </div>
                <Badge kind={c.paid ? 'green' : 'red'}>{c.paid ? 'PAID' : 'NOT PAID'}</Badge>
              </div>
              <div className="flex gap-1.5 flex-wrap mb-2.5">
                <Badge kind="yellow">{stage?.label || c.status}</Badge>
              </div>
              <div className="flex gap-3 text-xs text-gray-500">
                <span>Rev: <strong className="text-gray-900">${c.monthlyRevenue}</strong></span>
                <span>Cost: <strong className="text-gray-900">${c.monthlyCost}</strong></span>
                <span>Profit: <strong className="text-green-600">${profit}</strong></span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- MAIN COMPONENT ----------
export default function ClientPortal() {
  const [tab, setTab] = useState('prospects');

  const tabs = [
    { key: 'prospects', label: 'Prospects', count: PROSPECTS.length },
    { key: 'leads', label: 'Leads', count: LEADS.length },
    { key: 'clients', label: 'Clients', count: CLIENTS.length },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 m-0">Client Portal</h1>
          <p className="text-sm text-gray-600 mt-1.5">
            Unified prospect → lead → client lifecycle for Nicholas &amp; Caroline.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-1 bg-white border border-gray-200 rounded-lg shadow-sm p-1 mb-6 overflow-x-auto">
          {tabs.map(t => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex-1 min-w-[110px] px-4 py-2.5 rounded-md text-xs font-bold uppercase tracking-wider transition-colors ${
                  active
                    ? 'bg-apn-blue text-white'
                    : 'bg-transparent text-gray-500 hover:text-apn-blue hover:bg-blue-50'
                }`}
              >
                {t.label} <span className="opacity-70 font-medium">({t.count})</span>
              </button>
            );
          })}
        </div>

        {/* Views */}
        {tab === 'prospects' && <ProspectView />}
        {tab === 'leads' && <LeadView />}
        {tab === 'clients' && <ClientView />}
      </div>
    </div>
  );
}
